import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingBagIcon, UserIcon, HeartIcon, MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { isShopSubdomain } from '../../utils/domainDetection';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useCart } from '../../contexts/CartContext';
import { useLocation } from '../../contexts/LocationContext';
import { useCustomerAuthStore } from '../../stores/customerAuthStore';
import { useLikesStore } from '../../stores/likesStore';
import CartSidebar from '../CartSidebar';
import LocationModal from './LocationModal';
import WelcomeLocationPrompt from './WelcomeLocationPrompt';
import SearchOverlay from './SearchOverlay';

export default function ShopLayout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cartSummary, openCart } = useCart();
  const { deliveryInfo, isLocationModalOpen, openLocationModal, closeLocationModal } = useLocation();
  const { customer, isAuthenticated, logout, getCurrentCustomer } = useCustomerAuthStore();
  const { likedSkus } = useLikesStore();
  const totalItems = cartSummary?.item_count || 0;

  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load customer data on mount if token exists
  useEffect(() => {
    const token = localStorage.getItem('customer_access_token');
    if (token && !customer) {
      getCurrentCustomer();
    }
  }, [customer, getCurrentCustomer]);

  // Set shop page title
  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'ARKX : 1-3 Hour Delivery | Get Fashion & Lifestyle & Art In Hours Not Days!';
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // Handle scroll for header transparency
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shopHome = isShopSubdomain() ? '/' : '/shop';

  const handleSignOut = () => {
    logout();
    setShowProfileDropdown(false);
    navigate(shopHome);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm'
          : 'bg-transparent border-b border-transparent'
      }`}>
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-2 sm:gap-4">
            {/* Left: Logo - goes to shop home */}
            <Link to={shopHome} className="flex-shrink-0">
              <img
                src="/arkx_black.png"
                alt="ARKX"
                className="h-6 sm:h-7 w-auto"
              />
            </Link>

            {/* Location Pill - Hidden on mobile, visible on sm+ */}
            <button
              onClick={openLocationModal}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-black rounded-full text-xs text-white flex-shrink-0 hover:bg-gray-800 transition-colors"
            >
              <MapPinIcon className="h-3.5 w-3.5" />
              {deliveryInfo?.is_eligible ? (
                <>
                  <span className="font-medium">{deliveryInfo.zip_code}</span>
                  <span className="text-white/70">•</span>
                  <span className="text-green-400 font-medium">{deliveryInfo.eta_display || '30-60 min'}</span>
                </>
              ) : (
                <span className="font-medium">Set Location</span>
              )}
            </button>

            {/* Center: Search Bar Trigger - Hidden on mobile, shown in second row */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="hidden sm:flex flex-1 max-w-lg items-center gap-2 pl-4 pr-4 py-2 bg-gray-100 rounded-full text-sm text-gray-500 text-left hover:bg-gray-200/70 transition-colors"
            >
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">{searchQuery || 'Search'}</span>
            </button>

            {/* Right: Location (mobile), User, Heart, Cart Icons - Always visible */}
            <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
              {/* Mobile Location Icon - Only on mobile */}
              <button
                onClick={openLocationModal}
                className="sm:hidden text-gray-700 hover:text-gray-900 transition-colors relative"
                aria-label="Set delivery location"
              >
                <MapPinIcon className="h-5 w-5" />
                {deliveryInfo?.is_eligible && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" />
                )}
              </button>

              {/* User Icon / Profile */}
              {isAuthenticated ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="text-gray-700 hover:text-gray-900 transition-colors"
                    aria-label="Account menu"
                  >
                    <UserIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>

                  {/* Profile Dropdown */}
                  {showProfileDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-900">
                          {customer?.first_name} {customer?.last_name}
                        </div>
                        <div className="text-xs text-gray-500">{customer?.email}</div>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setShowProfileDropdown(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Profile
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/account"
                  className="text-gray-700 hover:text-gray-900 transition-colors"
                  aria-label="Sign in"
                >
                  <UserIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </Link>
              )}

              {/* Heart / Likes Icon - Show for all users */}
              <Link
                to="/likes"
                className="text-gray-700 hover:text-gray-900 transition-colors relative"
                aria-label="Liked items"
              >
                {likedSkus.length > 0 ? (
                  <HeartIconSolid className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
                ) : (
                  <HeartIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </Link>

              {/* Cart Icon */}
              <button
                onClick={openCart}
                className="text-gray-700 hover:text-gray-900 transition-colors relative"
                aria-label="Shopping cart"
              >
                <ShoppingBagIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                {totalItems > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-black text-white text-[10px] sm:text-xs font-bold rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Search Bar Trigger - Second row */}
          <button
            onClick={() => setIsSearchOpen(true)}
            className="sm:hidden w-full flex items-center gap-2 pl-4 pr-3 py-2 mb-2 bg-gray-100 rounded-full text-sm text-gray-500 text-left hover:bg-gray-200/70 transition-colors"
          >
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="truncate">{searchQuery || 'Search'}</span>
          </button>
        </nav>
      </header>

      {/* Spacer for fixed header (accounts for mobile search bar) */}
      <div className="h-[100px] sm:h-16" />

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer - Minimal */}
      <footer className="border-t border-gray-200 mt-auto">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} ARKX. All rights reserved.
            </p>

            {/* Links */}
            <div className="flex items-center gap-6 text-xs text-gray-500">
              <Link to="/privacy" className="hover:text-gray-900 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-gray-900 transition-colors">
                Terms
              </Link>
              <Link to="/return-policy" className="hover:text-gray-900 transition-colors">
                Returns
              </Link>
              <a href="https://portal.arkxla.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition-colors">
                Vendor Portal
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Cart Sidebar */}
      <CartSidebar />

      {/* Location Modal */}
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={closeLocationModal}
      />

      {/* First-visit Welcome Prompt */}
      <WelcomeLocationPrompt />

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        initialQuery={searchQuery}
        onSearch={(q) => {
          setSearchQuery(q);
          setIsSearchOpen(false);
          if (q.trim()) {
            navigate(`${shopHome}?q=${encodeURIComponent(q.trim())}`);
          } else {
            navigate(shopHome);
          }
        }}
      />
    </div>
  );
}
